import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  ArrowLeft, 
  Trash2, 
  Loader2,
  Mail,
  Calendar,
  FileText,
  Download,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  Archive,
  Send
} from 'lucide-react';
import { getSubmission, getForm, deleteSubmission, getFileUrl } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Safe date parsing
const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const configs = {
    submitted: { label: 'Eingereicht', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Send },
    processing: { label: 'In Bearbeitung', className: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
    completed: { label: 'Abgeschlossen', className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
    approved: { label: 'Genehmigt', className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
    rejected: { label: 'Abgelehnt', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
    archived: { label: 'Archiviert', className: 'bg-slate-100 text-slate-600 border-slate-200', icon: Archive },
  };
  
  const config = configs[status] || configs.submitted;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${config.className}`}>
      <Icon className="h-4 w-4" />
      {config.label}
    </span>
  );
};

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const submissionData = await getSubmission(id);
        console.log('Submission Data loaded:', submissionData);
        
        // Validate we have the full submission object
        if (!submissionData || !submissionData.id) {
          throw new Error('Invalid submission data');
        }
        
        setSubmission(submissionData);
        
        if (submissionData.form_id) {
          try {
            const formData = await getForm(submissionData.form_id);
            console.log('Form Data loaded:', formData);
            setForm(formData);
          } catch (e) {
            console.log('Could not fetch form details:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching submission:', error);
        toast.error('Einsendung nicht gefunden');
        navigate('/admin/submissions');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleDelete = async () => {
    try {
      await deleteSubmission(id);
      toast.success('Einsendung gelöscht');
      navigate('/admin/submissions');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/submissions/${id}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `einreichung_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('PDF Export fehlgeschlagen');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  const data = submission.data || {};
  const fields = form?.fields || [];

  // Build field_id to field mapping
  const fieldMap = {};
  fields.forEach(f => {
    fieldMap[f.field_id] = f;
  });

  const formatValue = (value, field) => {
    if (value === null || value === undefined) return '-';
    if (value === '') return '-';
    
    // Handle file uploads
    if (typeof value === 'object' && value.filename) {
      return {
        type: 'file',
        filename: value.filename,
        originalName: value.original_name || value.filename,
        url: value.url || `${BACKEND_URL}/api/uploads/submissions/${value.filename}`
      };
    }

    // Handle composite fields (Feldbündel)
    if (field?.field_type === 'composite' && typeof value === 'object' && !Array.isArray(value)) {
      const compositeFields = field.composite_fields || [];
      const separator = field.composite_separator || ' ';
      const parts = [];
      
      for (const subField of compositeFields.sort((a, b) => (a.order || 0) - (b.order || 0))) {
        const subValue = value[subField.field_id];
        if (subValue) {
          parts.push(String(subValue));
        }
      }
      
      return parts.length > 0 ? parts.join(separator) : '-';
    }

    // Handle date
    if (field?.field_type === 'date') {
      const d = safeDate(value);
      if (d) {
        try {
          return format(d, 'dd.MM.yyyy');
        } catch {
          return String(value);
        }
      }
      return String(value);
    }

    // Handle arrays (checkboxes or repeatable groups)
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      
      // Check if it's an array of objects (repeatable group)
      if (typeof value[0] === 'object') {
        return { type: 'repeatable_group', items: value, field };
      }
      
      // Array of strings (checkboxes)
      return value.join(', ');
    }

    return String(value);
  };

  // Get display data - either from fields or raw data
  const getDisplayData = () => {
    if (fields.length > 0) {
      return fields
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .filter(field => !['heading', 'info'].includes(field.field_type))
        .map(field => ({
          fieldId: field.field_id,
          label: field.label,
          value: formatValue(data[field.field_id], field),
          fieldType: field.field_type,
          field
        }))
        .filter(item => {
          // Keep items with actual values (strings, objects, arrays)
          if (item.value === null || item.value === undefined) return false;
          if (item.value === '-') return false;
          if (typeof item.value === 'object') return true; // Keep files, repeatable groups
          if (typeof item.value === 'string' && item.value.trim() !== '') return true;
          return false;
        });
    } else {
      // Fallback: show raw data with field IDs as labels
      return Object.entries(data)
        .filter(([key, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({
          fieldId: key,
          label: key,
          value: formatValue(value, null),
          fieldType: 'text'
        }));
    }
  };

  const displayData = getDisplayData();
  const titleValue = displayData.length > 0 ? displayData[0].value : 'Einsendung';

  const renderValue = (item) => {
    const { value, fieldType } = item;
    
    // Handle file
    if (typeof value === 'object' && value.type === 'file') {
      return (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-500" />
          <span>{value.originalName}</span>
          <a 
            href={value.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      );
    }

    // Handle repeatable group
    if (typeof value === 'object' && value.type === 'repeatable_group') {
      const groupFields = value.field?.group_fields || [];
      const groupFieldMap = {};
      groupFields.forEach(gf => { groupFieldMap[gf.field_id] = gf; });

      return (
        <div className="space-y-2">
          {value.items.map((groupItem, idx) => (
            <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
              <div className="text-xs text-gray-500 mb-2">#{idx + 1}</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(groupItem).map(([key, val]) => (
                  <div key={key}>
                    <span className="text-gray-500">{groupFieldMap[key]?.label || key}:</span>{' '}
                    <span className="font-medium">{val || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Regular value
    if (fieldType === 'textarea') {
      return <span className="whitespace-pre-wrap">{value}</span>;
    }

    return <span>{value}</span>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="submission-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/submissions">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {typeof titleValue === 'string' ? titleValue : 'Einsendung'}
            </h1>
            <p className="text-slate-600 mt-1">{submission.form_name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            PDF herunterladen
          </Button>
          <Button 
            variant="outline" 
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowDelete(true)}
            data-testid="delete-submission-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Löschen
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card className="border-slate-200/60 bg-gradient-to-r from-slate-50 to-white">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <StatusBadge status={submission.status || 'submitted'} />
              <div className="text-sm text-slate-500">
                ID: <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{submission.id.slice(0, 8)}...</code>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-slate-400" />
              {submission.email_sent ? (
                <span className="text-emerald-600 font-medium">E-Mail gesendet</span>
              ) : (
                <span className="text-amber-600">E-Mail ausstehend</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meta Info */}
      <Card className="border-slate-200/60">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-slate-400" />
              <span className="text-slate-500">Eingegangen:</span>
              <span className="font-medium">
                {submission.created_at 
                  ? (() => {
                      const d = safeDate(submission.created_at);
                      return d ? format(d, 'dd.MM.yyyy HH:mm', { locale: de }) : '-';
                    })()
                  : '-'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-slate-500">Formular:</span>
              <span className="font-medium">{submission.form_name || '-'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Data */}
      <Card className="border-slate-200/60">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Formulardaten</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-4">
            {displayData.map((item, index) => (
              <div key={index} className="border-b border-slate-100 pb-3">
                <dt className="text-sm text-slate-500 mb-1">{item.label}</dt>
                <dd className="font-medium text-slate-900">
                  {renderValue(item)}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* Raw Data (Debug) */}
      {fields.length === 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-slate-500">Rohdaten</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-slate-50 p-4 rounded overflow-auto">
              {JSON.stringify(data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Einsendung löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Die Einsendung wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
